import React, { useEffect, useRef, useState } from "react";
import Dialog from ".";
import useAuthedApi from "../../hooks/useAuthedApi";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import { IconLoading } from "../../assets/icons";
import OtpInput from "../commons/OtpInput";
import { useAuth } from "../../context/authContext";

const DialogVerifyRegister = ({
  isVerificationOpen,
  setVerificationOpen,
  userId,
  isLogin,
  email,
  isLoginVerification,
  onVerified,
}) => {
  const { fetchUser } = useAuth();

  const [loadingVerify, setLoadingVerify] = useState(false);
  const { authedPost } = useAuthedApi();
  const [verificationCode, setVerificationCode] = useState("");
  const { addToast } = useToast();
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // countdown in seconds
  // const [isLoginVerification, setIsLoginVerification] = useState(false);
  // const [userId, setUserId] = useState("");
  const navigate = useNavigate();

  // Resend verification email
  const resendVerificationEmail = async () => {
    try {
      setResendLoading(true);

      if (isLoginVerification) {
        // LOGIN FLOW
        await authedPost(`/api/verify-signin/resend`, {
          email,
          verification_token: userId,
        });
        addToast("success", "Login verification code resent.");
      } else {
        // SIGNUP FLOW
        await authedPost(`/api/verify/resend`, {
          email,
          verification_token: userId,
        });
        addToast("success", "Verification email resent successfully.");
      }

      // Save cooldown timestamp
      const cooldownUntil = Date.now() + 300000; // 5 minutes
      localStorage.setItem("resend_cooldown_until", cooldownUntil);

      setResendCooldown(300);
    } catch (err) {
      console.error("Failed to resend verification:", err);
      addToast(
        "error",
        err?.response?.data?.error ??
          err?.response?.data?.message ??
          "Failed to resend verification email.",
      );
    } finally {
      setResendLoading(false);
    }
  };

  // Countdown effect (decrease every second)
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem("resend_cooldown_until");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Automatically start countdown when dialog opens (optional)
  useEffect(() => {
    if (!isVerificationOpen) return;

    const saved = localStorage.getItem("resend_cooldown_until");

    if (saved) {
      const remaining = Math.floor((Number(saved) - Date.now()) / 1000);

      if (remaining > 0) {
        setResendCooldown(remaining);
      } else {
        setResendCooldown(0);
        localStorage.removeItem("resend_cooldown_until");
      }
    } else {
      setResendCooldown(0); // first time → ENABLED
    }
  }, [isVerificationOpen]);

  const handleVerifyCode = async () => {
    try {
      setLoadingVerify(true);

      if (isLoginVerification) {
        // LOGIN VERIFICATION (new API)
        await authedPost("/api/verify-signin/", {
          verification_token: userId,
          code: verificationCode,
        });

        addToast("success", "Verification successful. You can now login.");
        setVerificationOpen(false);
        localStorage.removeItem("verification_code_signin");
        localStorage.removeItem("verification_code_signup");
        await fetchUser(); // Refetch user data to update context/state
        navigate("/dashboard");
        return;
      }

      const response = await authedPost(`/api/verify-code/`, {
        verification_token: userId,
        code: verificationCode,
      });

      if (response.data.message) {
        if (isLogin) {
          addToast(
            "success",
            "Verification successful. Please re-enter your credentials.",
            5000,
          );
        } else {
          addToast(
            "success",
            "Email verified successfully! Finalizing your account...",
            500,
          );
          addToast("success", "Account ready! You can now log in.", 500);
        }
        localStorage.removeItem("verification_code_signin");
        localStorage.removeItem("verification_code_signup");
        setVerificationOpen(false);
        if (!isLogin && onVerified) {
          onVerified();
        } else if (!isLogin) {
          setTimeout(() => navigate("/login"), 1000);
        }
      }
    } catch (error) {
      setError(
        error?.response?.data?.error ??
          "Invalid verification code. Please try again.",
      );
    } finally {
      setLoadingVerify(false);
    }
  };

  // Format seconds into mm:ss
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // useEffect(() => {
  //   if (!isVerificationOpen) return;

  //   const signup = localStorage.getItem("verification_code_signup");
  //   const signin = localStorage.getItem("verification_code_signin");

  //   if (signin) setUserId(signin);
  //   else if (signup) setUserId(signup);
  // }, [isVerificationOpen]);
  useEffect(() => {
    if (!isVerificationOpen) {
      setError("");
      setVerificationCode("");
    }
  }, [isVerificationOpen]);

  useEffect(() => {
    if (!isVerificationOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        if (verificationCode.length === 6 && !loadingVerify) {
          handleVerifyCode();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [verificationCode, loadingVerify, isVerificationOpen]);

  return (
    <Dialog
      open={isVerificationOpen}
      onClose={() => setVerificationOpen(false)}
      width="450px"
      className="!bg-[#FFFFFF] !ring-1 !ring-[#D9D9D9]"
    >
      <div className="bg-white text-center">
        <h3 className="text-xl font-semibold mb-2">Enter Verification Code</h3>
        {isLoginVerification ? (
          <p className="text-gray-500 mb-6 text-sm">
            We’ve sent a 6-digit verification code to your email to complete
            your login.
          </p>
        ) : isLogin ? (
          <p className="text-gray-500 mb-6 text-sm">
            Your account hasn’t been verified yet. We’ve sent a 6-digit
            verification code to your email. Please enter it to complete your
            registration before logging in.
          </p>
        ) : (
          <p className="text-gray-500 mb-6 text-sm">
            We’ve sent a 6-digit verification code to your email. Please enter
            the code below to verify and activate your account.
          </p>
        )}

        <OtpInput length={6} setVerificationCode={setVerificationCode} />

        {error && (
          <p className="text-red-600 text-left text-xs mt-2">{error}</p>
        )}

        <button
          onClick={handleVerifyCode}
          disabled={verificationCode.length < 6 || loadingVerify}
          className="w-full relative overflow-hidden !py-3 !font-bold !text-base !bg-[#007A78]"
        >
          {loadingVerify ? (
            <div className="animate-spin w-fit flex justify-center items-center mx-auto">
              <IconLoading className="w-[24px] h-[24px]" />
            </div>
          ) : (
            "Verify"
          )}
        </button>

        {/* Manual resend button with countdown */}
        <button
          onClick={resendVerificationEmail}
          disabled={resendLoading || resendCooldown > 0}
          className="w-full mt-3 text-sm text-[#007A78] underline !bg-transparent"
        >
          {resendLoading
            ? "Resending..."
            : resendCooldown > 0
              ? `Resend verification email (${formatTime(resendCooldown)})`
              : "Resend verification email"}
        </button>
      </div>
    </Dialog>
  );
};

export default DialogVerifyRegister;
